import { motion } from 'framer-motion';
import type { ReservationState } from '@swi/contracts';

export const cx = (...values: Array<string | false | null | undefined>): string =>
  values.filter(Boolean).join(' ');

/* ----------------------------------- Card ---------------------------------- */

export function Card({
  children,
  className,
  as: Tag = 'section',
}: {
  children: React.ReactNode;
  className?: string;
  as?: 'section' | 'div' | 'article';
}) {
  return <Tag className={cx('glass p-5', className)}>{children}</Tag>;
}

export function CardHeader({
  title,
  subtitle,
  action,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <header className="mb-4 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-base font-semibold text-[var(--text-primary)]">{title}</h2>
        {subtitle !== undefined && (
          <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">{subtitle}</p>
        )}
      </div>
      {action}
    </header>
  );
}

/* ---------------------------------- Button --------------------------------- */

type ButtonVariant = 'primary' | 'ghost' | 'outline' | 'danger' | 'warn';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary:
    'bg-[var(--accent)] text-[var(--text-inverse)] hover:bg-[var(--accent-strong)] shadow-[0_0_24px_-8px_var(--accent-glow)]',
  ghost:
    'bg-transparent text-[var(--text-secondary)] hover:bg-[var(--surface-glass-strong)] hover:text-[var(--text-primary)]',
  outline:
    'bg-[var(--surface-glass)] text-[var(--text-primary)] border border-[var(--border-strong)] hover:bg-[var(--surface-glass-strong)]',
  danger: 'bg-[var(--danger)] text-[var(--text-inverse)] hover:brightness-110',
  warn: 'bg-[var(--warn)] text-[var(--text-inverse)] hover:brightness-110',
};

export const buttonClass = (
  variant: ButtonVariant = 'outline',
  size: 'sm' | 'md' = 'md',
  extra?: string,
): string =>
  cx(
    'inline-flex items-center justify-center gap-2 rounded-[var(--radius)] font-medium transition-all',
    size === 'sm' ? 'px-3 py-1.5 text-[13px]' : 'px-4 py-2 text-sm',
    BUTTON_STYLES[variant],
    extra,
  );

export function Button({
  variant = 'outline',
  size = 'md',
  loading = false,
  className,
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: 'sm' | 'md';
  loading?: boolean;
}) {
  return (
    <button
      {...rest}
      disabled={rest.disabled === true || loading}
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-[var(--radius)] font-medium transition-all',
        'disabled:cursor-not-allowed disabled:opacity-50',
        size === 'sm' ? 'px-3 py-1.5 text-[13px]' : 'px-4 py-2 text-sm',
        BUTTON_STYLES[variant],
        className,
      )}
    >
      {loading && (
        <span
          aria-hidden
          className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      )}
      {children}
    </button>
  );
}

/* ---------------------------------- Badge ---------------------------------- */

const STATE_TOKEN: Record<ReservationState, string> = {
  DRAFT: 'var(--state-draft)',
  PENDING_APPROVAL: 'var(--state-pending-approval)',
  CONFIRMED: 'var(--state-confirmed)',
  IN_FLIGHT: 'var(--state-in-flight)',
  COMPLETED: 'var(--state-completed)',
  CANCELLED: 'var(--state-cancelled)',
  REJECTED: 'var(--state-rejected)',
  EXPIRED: 'var(--state-expired)',
};

export const STATE_LABEL: Record<ReservationState, string> = {
  DRAFT: 'Koncept',
  PENDING_APPROVAL: 'Čeká na schválení',
  CONFIRMED: 'Potvrzeno',
  IN_FLIGHT: 'Ve vzduchu',
  COMPLETED: 'Dokončeno',
  CANCELLED: 'Zrušeno',
  REJECTED: 'Zamítnuto',
  EXPIRED: 'Propadlo',
};

export function stateColor(state: ReservationState): string {
  return STATE_TOKEN[state];
}

export function StateBadge({ state, className }: { state: ReservationState; className?: string }) {
  const color = STATE_TOKEN[state];
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 rounded-[var(--radius-full)] px-2.5 py-0.5 text-[11px] font-semibold tracking-wide uppercase',
        className,
      )}
      style={{ color, background: `color-mix(in srgb, ${color} 14%, transparent)` }}
    >
      <span className="size-1.5 rounded-full" style={{ background: color }} aria-hidden />
      {STATE_LABEL[state]}
    </span>
  );
}

export function Pill({
  children,
  tone = 'neutral',
  className,
}: {
  children: React.ReactNode;
  tone?: 'neutral' | 'accent' | 'warn' | 'danger' | 'info' | 'violet';
  className?: string;
}) {
  const map = {
    neutral: ['var(--text-secondary)', 'var(--surface-glass-strong)'],
    accent: ['var(--accent)', 'var(--accent-dim)'],
    warn: ['var(--warn)', 'var(--warn-dim)'],
    danger: ['var(--danger)', 'var(--danger-dim)'],
    info: ['var(--info)', 'var(--info-dim)'],
    violet: ['var(--violet)', 'var(--violet-dim)'],
  } as const;
  const [color, background] = map[tone];
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1 rounded-[var(--radius-full)] px-2 py-0.5 text-[11px] font-medium',
        className,
      )}
      style={{ color, background }}
    >
      {children}
    </span>
  );
}

/* ---------------------------------- Inputs --------------------------------- */

export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <label className={cx('block', className)}>
      <span className="label-caps mb-1.5 block">{label}</span>
      {children}
      {error !== undefined ? (
        <span className="mt-1 block text-[12px] text-[var(--danger)]">{error}</span>
      ) : (
        hint !== undefined && (
          <span className="mt-1 block text-[12px] text-[var(--text-muted)]">{hint}</span>
        )
      )}
    </label>
  );
}

const CONTROL =
  'w-full rounded-[var(--radius)] border border-[var(--border-subtle)] bg-[var(--surface-glass)] px-3 py-2 text-sm text-[var(--text-primary)] transition-colors placeholder:text-[var(--text-muted)] hover:border-[var(--border-strong)] focus:border-[var(--accent)] focus:outline-none';

export const Input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input {...props} className={cx(CONTROL, props.className)} />
);

export const Select = (props: React.SelectHTMLAttributes<HTMLSelectElement>) => (
  <select {...props} className={cx(CONTROL, 'appearance-none pr-8', props.className)} />
);

export const Textarea = (props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
  <textarea {...props} className={cx(CONTROL, 'min-h-20 resize-y', props.className)} />
);

/* --------------------------------- Feedback -------------------------------- */

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cx(
        'animate-pulse rounded-[var(--radius)] bg-[var(--surface-glass-strong)]',
        className,
      )}
    />
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-[var(--radius-lg)] border border-dashed border-[var(--border-subtle)] px-6 py-12 text-center">
      <p className="font-medium text-[var(--text-primary)]">{title}</p>
      {description !== undefined && (
        <p className="max-w-md text-[13px] text-[var(--text-secondary)]">{description}</p>
      )}
      {action}
    </div>
  );
}

export function ErrorNote({ title, detail }: { title: string; detail?: string }) {
  return (
    <div
      role="alert"
      className="rounded-[var(--radius)] border border-[var(--danger)] px-3 py-2 text-[13px]"
      style={{ background: 'var(--danger-dim)' }}
    >
      <p className="font-medium text-[var(--danger)]">{title}</p>
      {detail !== undefined && (
        <p className="mono mt-1 text-[11px] text-[var(--text-secondary)]">{detail}</p>
      )}
    </div>
  );
}

/* ----------------------------------- KPI ----------------------------------- */

export function Kpi({
  label,
  value,
  unit,
  tone = 'neutral',
  hint,
  title,
}: {
  label: string;
  value: React.ReactNode;
  unit?: string;
  tone?: 'neutral' | 'accent' | 'warn' | 'danger' | 'info';
  hint?: string;
  /** Exact figure behind an abbreviated value. */
  title?: string;
}) {
  // A currency figure is much wider than a count, so a long value steps the
  // type down instead of being clipped by the tile.
  const text = typeof value === 'string' ? value : '';
  const fontSize =
    text.length > 11
      ? 'clamp(0.95rem, 0.7rem + 0.7vw, 1.2rem)'
      : text.length > 7
        ? 'clamp(1.1rem, 0.85rem + 0.8vw, 1.45rem)'
        : undefined;

  const color =
    tone === 'accent'
      ? 'var(--accent)'
      : tone === 'warn'
        ? 'var(--warn)'
        : tone === 'danger'
          ? 'var(--danger)'
          : tone === 'info'
            ? 'var(--info)'
            : 'var(--text-primary)';

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass flex min-w-0 flex-col gap-1 p-4"
      title={title}
    >
      <span className="label-caps truncate">{label}</span>
      <span className="kpi-value truncate" style={{ color, fontSize }}>
        {value}
        {unit !== undefined && (
          <span className="ml-1 text-sm font-medium text-[var(--text-muted)]">{unit}</span>
        )}
      </span>
      {hint !== undefined && <span className="text-[12px] text-[var(--text-muted)]">{hint}</span>}
    </motion.div>
  );
}

/* ---------------------------------- Modal ---------------------------------- */

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  if (!open) {
    return null;
  }
  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 backdrop-blur-sm sm:items-center"
      onClick={onClose}
      role="presentation"
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        initial={{ opacity: 0, scale: 0.97, y: 12 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="glass-strong w-full max-w-lg p-5"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{title}</h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Zavřít">
            ✕
          </Button>
        </div>
        {children}
        {footer !== undefined && <div className="mt-5 flex justify-end gap-2">{footer}</div>}
      </motion.div>
    </div>
  );
}
